// use-cookie-value.ts — Cookies suite, subscription
//
// Typed read/write of a single cookie keyed by subPath. Handles JSON parse/serialize
// for object values; preserves string values verbatim. Subscribes to provider-level
// notifications so multiple consumers reading the same cookie stay in sync.

'use client';

import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useCookiesContext } from './CookiesProvider';

type CookieObjectValue = Record<string, unknown>;
type CookieValue = string | CookieObjectValue;
type CookieUpdater<TValue extends CookieValue> = TValue | ((previousValue: TValue) => TValue);

interface UseCookieValueParams<TValue extends CookieValue> {
  subPath: string;
  defaultValue: TValue;
}

interface UseCookieValueReturn<TValue extends CookieValue> {
  value: TValue;
  setValue: (nextValueOrUpdater: CookieUpdater<TValue>) => void;
  removeValue: () => void;
}

function isObjectValue(value: unknown): value is CookieObjectValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseCookieValue<TValue extends CookieValue>(input: {
  defaultValue: TValue;
  rawValue: string | undefined;
}): TValue {
  const { defaultValue, rawValue } = input;
  if (typeof rawValue !== 'string') return defaultValue;
  if (typeof defaultValue === 'string') return rawValue as TValue;
  return parseObjectCookieValue({
    defaultValue: defaultValue as CookieObjectValue,
    rawValue,
  }) as TValue;
}

function parseObjectCookieValue<TValue extends CookieObjectValue>(input: {
  defaultValue: TValue;
  rawValue: string;
}): TValue {
  const { defaultValue, rawValue } = input;
  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    return isObjectValue(parsedValue) ? (parsedValue as TValue) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function serializeCookieValue<TValue extends CookieValue>(value: TValue): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function getServerCookieSnapshot(): undefined {
  return undefined;
}

export function useCookieValue<TValue extends CookieValue>({
  subPath,
  defaultValue,
}: UseCookieValueParams<TValue>): UseCookieValueReturn<TValue> {
  const { getCookie, setCookie, removeCookie, subscribeToCookie } = useCookiesContext();
  // getSnapshot must return a CACHED value (React's useSyncExternalStore
  // contract): document.cookie is not a stable store — the browser updates
  // the jar concurrently (e.g. a network response's Set-Cookie), so two
  // same-render reads can differ, which React flags as an infinite-loop
  // hazard. The snapshot therefore reads the jar only on subscription
  // notifications (and when the subPath changes), never per render.
  const snapshotSubPathRef = useRef<string | null>(null);
  const snapshotValueRef = useRef<string | undefined>(undefined);
  const readSnapshot = useCallback((): string | undefined => {
    snapshotSubPathRef.current = subPath;
    snapshotValueRef.current = getCookie(subPath);
    return snapshotValueRef.current;
  }, [getCookie, subPath]);
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      subscribeToCookie(subPath, () => {
        readSnapshot();
        onStoreChange();
      }),
    [readSnapshot, subPath, subscribeToCookie],
  );
  const getSnapshot = useCallback(() => {
    if (snapshotSubPathRef.current !== subPath) {
      return readSnapshot();
    }
    return snapshotValueRef.current;
  }, [readSnapshot, subPath]);
  const rawValue = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerCookieSnapshot,
  );

  const value = useMemo(
    () => parseCookieValue({ defaultValue, rawValue }),
    [defaultValue, rawValue],
  );

  const setValue = useCallback(
    (nextValueOrUpdater: CookieUpdater<TValue>) => {
      const currentRawValue = getCookie(subPath);
      const currentValue = parseCookieValue({
        defaultValue,
        rawValue: typeof currentRawValue === 'string' ? currentRawValue : rawValue,
      });
      const nextValue =
        typeof nextValueOrUpdater === 'function' ? nextValueOrUpdater(currentValue) : nextValueOrUpdater;
      const nextRawValue = serializeCookieValue(nextValue);
      if (currentRawValue === nextRawValue) {
        return;
      }
      setCookie(subPath, nextRawValue);
    },
    [defaultValue, getCookie, rawValue, setCookie, subPath],
  );

  const removeValue = useCallback(() => {
    removeCookie(subPath);
  }, [removeCookie, subPath]);

  return useMemo(
    () => ({ value, setValue, removeValue }),
    [removeValue, setValue, value],
  );
}
